import type { LanguageFn } from 'highlight.js';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import plaintext from 'highlight.js/lib/languages/plaintext';
import python from 'highlight.js/lib/languages/python';
import ruby from 'highlight.js/lib/languages/ruby';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';

export const CORE_LANGUAGE_GRAMMARS: Record<string, LanguageFn> = {
  bash,
  css,
  javascript,
  json,
  markdown,
  plaintext,
  python,
  ruby,
  sql,
  typescript,
  xml,
  yaml
};

export type LanguageOption = { id: string; label: string };

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { id: 'plaintext', label: 'Plain text' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'python', label: 'Python' },
  { id: 'ruby', label: 'Ruby' },
  { id: 'bash', label: 'Bash / Shell' },
  { id: 'json', label: 'JSON' },
  { id: 'yaml', label: 'YAML' },
  { id: 'css', label: 'CSS' },
  { id: 'xml', label: 'HTML / XML' },
  { id: 'sql', label: 'SQL' },
  { id: 'markdown', label: 'Markdown' },
  { id: 'java', label: 'Java' },
  { id: 'c', label: 'C' },
  { id: 'cpp', label: 'C++' },
  { id: 'csharp', label: 'C#' },
  { id: 'go', label: 'Go' },
  { id: 'rust', label: 'Rust' },
  { id: 'php', label: 'PHP' },
  { id: 'kotlin', label: 'Kotlin' },
  { id: 'swift', label: 'Swift' },
  { id: 'scss', label: 'SCSS' },
  { id: 'graphql', label: 'GraphQL' },
  { id: 'diff', label: 'Diff' }
];

type LowlightLike = { registered: (id: string) => boolean; register: (grammars: Record<string, LanguageFn>) => void };

const EXTRA_LANGUAGE_LOADERS: Record<string, () => Promise<{ default: LanguageFn }>> = {
  java: () => import(/* webpackChunkName: "hljs-lang-java" */ 'highlight.js/lib/languages/java'),
  c: () => import(/* webpackChunkName: "hljs-lang-c" */ 'highlight.js/lib/languages/c'),
  cpp: () => import(/* webpackChunkName: "hljs-lang-cpp" */ 'highlight.js/lib/languages/cpp'),
  csharp: () => import(/* webpackChunkName: "hljs-lang-csharp" */ 'highlight.js/lib/languages/csharp'),
  go: () => import(/* webpackChunkName: "hljs-lang-go" */ 'highlight.js/lib/languages/go'),
  rust: () => import(/* webpackChunkName: "hljs-lang-rust" */ 'highlight.js/lib/languages/rust'),
  php: () => import(/* webpackChunkName: "hljs-lang-php" */ 'highlight.js/lib/languages/php'),
  kotlin: () => import(/* webpackChunkName: "hljs-lang-kotlin" */ 'highlight.js/lib/languages/kotlin'),
  swift: () => import(/* webpackChunkName: "hljs-lang-swift" */ 'highlight.js/lib/languages/swift'),
  scss: () => import(/* webpackChunkName: "hljs-lang-scss" */ 'highlight.js/lib/languages/scss'),
  graphql: () => import(/* webpackChunkName: "hljs-lang-graphql" */ 'highlight.js/lib/languages/graphql'),
  diff: () => import(/* webpackChunkName: "hljs-lang-diff" */ 'highlight.js/lib/languages/diff')
};

const loaded = new Set<string>(Object.keys(CORE_LANGUAGE_GRAMMARS));

export async function ensureLanguageLoaded(lowlight: LowlightLike, id: string): Promise<void> {
  if (loaded.has(id) || lowlight.registered(id)) {
    loaded.add(id);
    return;
  }
  const load = EXTRA_LANGUAGE_LOADERS[id];
  if (!load) return;
  const mod = await load();
  lowlight.register({ [id]: mod.default });
  loaded.add(id);
}
